import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ShieldCheck, Copy } from 'lucide-react'
import QRCode from 'qrcode'
import { toast } from 'sonner'

const SETUP_TOKEN_KEY = 'roi365_mfa_setup_token'
const API_HOST = import.meta.env.VITE_API_URL ?? ''
const BASE_URL = `${API_HOST}/api`

type SetupResponse = { secret: string; otpauth: string }
type VerifyResponse = {
  user: { id: string; email: string; name: string; role: string }
}

// El setupToken es un JWT corto (15 min) específico para este flujo de
// alta de MFA. Va como Bearer en Authorization porque la cookie de
// sesión todavía no existe (no se emite hasta /mfa/forced-verify).
async function authedFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const setupToken = sessionStorage.getItem(SETUP_TOKEN_KEY)
  if (!setupToken) throw new Error('Sesion de configuracion expirada')
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${setupToken}`,
      ...(init.headers as Record<string, string> | undefined),
    },
  })
  const json = await res.json()
  if (!res.ok) {
    throw new Error(json?.error?.message ?? 'Request failed')
  }
  return json.data as T
}

export function LoginMfaSetupPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [setup, setSetup] = useState<SetupResponse | null>(null)
  const [code, setCode] = useState('')

  const setupMutation = useMutation({
    mutationFn: () => authedFetch<SetupResponse>('/auth/mfa/forced-setup', { method: 'POST' }),
    onSuccess: (data) => setSetup(data),
    onError: (err) => toast.error((err as Error).message),
  })

  const verifyMutation = useMutation({
    mutationFn: () =>
      authedFetch<VerifyResponse>('/auth/mfa/forced-verify', {
        method: 'POST',
        body: JSON.stringify({ code: code.replace(/\s+/g, '') }),
      }),
    onSuccess: () => {
      // La API ya ha emitido las cookies access/refresh; el setupToken
      // expira solo. Invalidamos la query de /auth/me para que el shell
      // recargue el perfil tras el cambio de sesión.
      sessionStorage.removeItem(SETUP_TOKEN_KEY)
      queryClient.invalidateQueries({ queryKey: ['auth'] })
      toast.success('MFA activado. Bienvenido.')
      navigate({ to: '/' })
    },
    onError: (err) => toast.error((err as Error).message),
  })

  useEffect(() => {
    const token = sessionStorage.getItem(SETUP_TOKEN_KEY)
    if (!token) {
      navigate({ to: '/login' })
      return
    }
    setupMutation.mutate()
  }, [])

  useEffect(() => {
    if (!setup) {
      setQrDataUrl(null)
      return
    }
    QRCode.toDataURL(setup.otpauth, { width: 220, margin: 1 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null))
  }, [setup])

  const cancel = () => {
    sessionStorage.removeItem(SETUP_TOKEN_KEY)
    navigate({ to: '/login' })
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center px-4"
      style={{ backgroundColor: 'var(--color-background)' }}
    >
      <div
        className="w-full max-w-lg rounded-xl border p-8"
        style={{
          backgroundColor: 'var(--color-surface)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <div className="mb-6 flex flex-col items-center">
          <img src="/logo-ROI365.svg" alt="ROI365" className="w-full max-w-xs object-contain" />
          <h2 className="mt-6 text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Configuracion obligatoria de MFA
          </h2>
          <p className="mt-2 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Por politica de seguridad, debes activar la autenticacion en dos pasos antes de acceder a la plataforma.
          </p>
        </div>

        {!setup ? (
          <div className="flex h-[260px] items-center justify-center">
            <div
              className="h-32 w-32 animate-pulse rounded-md"
              style={{ backgroundColor: 'var(--color-gray-100)' }}
            />
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              1. Escanea el QR con Microsoft Authenticator, Google Authenticator o 1Password.
            </p>
            <div className="flex flex-col items-start gap-4 md:flex-row">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="QR MFA"
                  width={220}
                  height={220}
                  className="rounded-md border"
                  style={{ borderColor: 'var(--color-border)' }}
                />
              ) : (
                <div
                  className="h-[220px] w-[220px] animate-pulse rounded-md"
                  style={{ backgroundColor: 'var(--color-gray-100)' }}
                />
              )}
              <div className="flex-1 space-y-3">
                <div>
                  <p
                    className="text-xs font-medium uppercase tracking-wider"
                    style={{ color: 'var(--color-text-tertiary)' }}
                  >
                    Clave manual
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <code
                      className="rounded-md border px-2 py-1 text-xs font-mono"
                      style={{
                        borderColor: 'var(--color-border)',
                        backgroundColor: 'var(--color-gray-50)',
                      }}
                    >
                      {setup.secret.match(/.{1,4}/g)?.join(' ')}
                    </code>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(setup.secret)
                        toast.success('Clave copiada')
                      }}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium hover:bg-gray-50"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      <Copy className="h-3.5 w-3.5" /> Copiar
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    2. Introduce el codigo de 6 digitos para confirmar.
                  </p>
                </div>
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (code.length !== 6) return
                verifyMutation.mutate()
              }}
              className="space-y-3"
            >
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                autoFocus
                className="focus-ring w-full rounded-md border px-3 py-2.5 text-center text-lg tabular-nums tracking-widest"
                style={{ borderColor: 'var(--color-border)' }}
              />
              <button
                type="submit"
                disabled={code.length !== 6 || verifyMutation.isPending}
                className="focus-ring inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-brand-secondary)' }}
              >
                <ShieldCheck className="h-4 w-4" />
                {verifyMutation.isPending ? 'Verificando...' : 'Verificar y acceder'}
              </button>
              <button
                type="button"
                onClick={cancel}
                className="w-full rounded-md px-4 py-2 text-sm font-medium hover:bg-gray-50"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Cancelar
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
